import { Globe, Users, CreditCard, Lock } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import {
  VISIBILITY_ORDER,
  visibilityRank,
  type Visibility
} from '@/types/access'

const ICONS: Record<Visibility, typeof Globe> = {
  public: Globe,
  members: Users,
  plan: CreditCard,
  private: Lock
}

interface VisibilityPickerProps {
  value: Visibility
  onChange: (value: Visibility) => void
  disabled?: boolean
  /**
   * The section this space lives in. A space may never be more open than its
   * section, so the options above it are drawn **disabled rather than hidden**:
   * a missing option reads as a bug, a greyed one with a reason next to it
   * reads as a rule.
   */
  sectionVisibility?: Visibility
  sectionTitle?: string
  className?: string
}

export function VisibilityPicker({
  value,
  onChange,
  disabled,
  sectionVisibility,
  sectionTitle,
  className
}: VisibilityPickerProps) {
  const { t } = useTranslation(['common'])

  const floor = sectionVisibility ? visibilityRank(sectionVisibility) : 0

  return (
    <div className={cn('space-y-2', className)}>
      <label className="text-sm font-medium">{t('common:fields.visibility')}</label>

      <div className="flex flex-wrap gap-2" role="radiogroup">
        {VISIBILITY_ORDER.map((option) => {
          const Icon = ICONS[option]
          const tooOpen = visibilityRank(option) < floor
          const isDisabled = disabled || tooOpen

          return (
            <button
              key={option}
              type="button"
              role="radio"
              aria-checked={value === option}
              disabled={isDisabled}
              title={tooOpen && sectionTitle
                ? t('common:visibility.tooOpenForSection', { section: sectionTitle })
                : t(`common:visibility.hints.${option}`)}
              onClick={() => onChange(option)}
              className={cn(
                'flex items-center gap-2 px-3 py-2 rounded-lg transition-colors text-sm',
                value === option
                  ? 'bg-primary-100 text-primary-700'
                  : 'bg-gray-100 hover:bg-gray-200',
                isDisabled && 'opacity-50 cursor-not-allowed'
              )}
            >
              <Icon className="w-4 h-4 flex-shrink-0" />
              <span>{t(`common:visibility.${option}`)}</span>
            </button>
          )
        })}
      </div>

      <p className="text-xs text-gray-500">{t(`common:visibility.hints.${value}`)}</p>

      {sectionTitle && floor > 0 && (
        <p className="text-xs text-gray-500">
          {t('common:visibility.sectionFloor', { section: sectionTitle })}
        </p>
      )}
    </div>
  )
}
